import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { Button, Card, Chip, Field, Input, Note, Empty, Skeleton } from '../ui';
import { fmtDate } from '../utils/date';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace(/\/api\/v1\/?$/, '');

/** Албан бичиг — Docx (docx.gov.mn) маягийн бүтэн дэлгэцийн засварлагч: зүүн талд бичгийн мэдээлэл,
 *  баруун талд форматтай бичвэр, дээр нь боловсруулах → дугаарлах → гарын үсэг → илгээх шатлал. */

type Rec = { name: string; reg_no?: string; branch_id?: number };
type Doc = {
  id: string; direction: 'out' | 'in'; status: string; doc_number: string; doc_date: string;
  subject: string; body: string; page_count: number; blank_type: string; doc_kind: string;
  reviewer_name: string; signer_name: string; signer_title: string; recipients: Rec[]; sender_name: string;
  has_file: boolean; file_name: string; digidoc_doc_no: string; sent_at: string | null;
  send_error: string; resolution_status: number; resolution_note: string; note: string;
};
type Cfg = { digidoc_enabled: boolean; docx_system_enabled?: boolean; docx_linked?: boolean; docx_org_id?: number; branch_id: number; next_number: string; org_name: string; reg_no: string; address: string; phone: string; email: string; hint: string };

const ST: Record<string, { label: string; tone: 'paid' | 'debt' | 'progress' | 'new' }> = {
  draft: { label: 'Боловсруулж байгаа', tone: 'new' }, ready: { label: 'Дугаар олгосон', tone: 'progress' },
  signed: { label: 'Гарын үсэгтэй', tone: 'progress' }, sent: { label: 'Илгээсэн', tone: 'paid' },
  received: { label: 'Ирсэн', tone: 'progress' }, resolved: { label: 'Шийдвэрлэсэн', tone: 'paid' },
  cancelled: { label: 'Цуцалсан', tone: 'debt' },
};
const STEPS = [
  { key: 'draft', label: 'БОЛОВСРУУЛЖ БАЙГАА' },
  { key: 'ready', label: 'ДУГААР ОЛГОХ' },
  { key: 'signed', label: 'ГАРЫН ҮСЭГ ЗУРАХ' },
  { key: 'sent', label: 'ИЛГЭЭХ' },
];
const KINDS = ['Албан бичиг', 'Хүсэлт', 'Мэдэгдэл', 'Тодорхойлолт', 'Тайлбар', 'Санамж бичиг'];
const RES = ['Бүртгэсэн', 'Илгээгдсэн', 'Судалж байгаа', 'Шийдвэрлэсэн'];

export default function OfficialDocsPage() {
  const [tab, setTab] = useState<'out' | 'in'>('out');
  const [docs, setDocs] = useState<Doc[] | null>(null);
  const [cfg, setCfg] = useState<Cfg | null>(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Partial<Doc> | null>(null);   // засварлагч нээлттэй бичиг
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([
        api.get<any>(`/admin/official-documents?direction=${tab}`),
        api.get<Cfg>('/admin/official-documents/config'),
      ]);
      setDocs(d.items || []); setCfg(c);
    } catch (e: any) { setErr(e?.message || 'Уншиж чадсангүй'); setDocs([]); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const note = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };
  const fail = (e: any, d: string) => { let t = d; try { t = JSON.parse(e?.body || '{}')?.detail || d; } catch { t = e?.message || d; } setErr(t); setTimeout(() => setErr(''), 6000); };

  const remove = async (d: Doc) => {
    if (!window.confirm(`${d.doc_number || ''} «${d.subject}» бичгийг устгах уу?`)) return;
    try { await api.delete(`/admin/official-documents/${d.id}`); load(); note('Устгалаа'); } catch (e) { fail(e, 'Устгаж чадсангүй'); }
  };
  const setStatus = async (d: Doc, status: number) => {
    setBusy(d.id);
    try { await api.post(`/admin/official-documents/${d.id}/status`, { status, note: '' }); load(); note('Төлөв шинэчлэв'); }
    catch (e) { fail(e, 'Төлөв хадгалж чадсангүй'); } finally { setBusy(''); }
  };
  const sync = async (kind: 'inbox' | 'sent') => {
    setBusy(kind);
    try {
      const r: any = await api.post(`/admin/official-documents/${kind}/sync`, {});
      load(); note(kind === 'inbox' ? `Docx-оос ${r?.added ?? 0} шинэ бичиг татлаа` : `${r?.updated ?? 0} бичгийн төлөв шинэчлэгдлээ`);
    } catch (e) { fail(e, 'Docx-оос татаж чадсангүй'); } finally { setBusy(''); }
  };
  const unlink = async () => {
    if (!window.confirm('Docx токеныг салгах уу? Дахин холбох хүртэл бичиг Docx-оор илгээгдэхгүй.')) return;
    try { await api.delete('/admin/official-documents/docx/link'); load(); note('Docx токен салгалаа'); } catch (e) { fail(e, 'Салгаж чадсангүй'); }
  };
  const openFile = async (d: Doc) => {
    try { const r: any = await api.get(`/admin/official-documents/${d.id}/file`); if (r?.url) window.open(r.url, '_blank'); }
    catch { window.open(`${API_ORIGIN}/api/v1/admin/official-documents/${d.id}/file`, '_blank'); }
  };

  if (open) return (
    <DocEditor doc={open} cfg={cfg} direction={tab}
      onClose={() => { setOpen(null); load(); }}
      onMsg={note} onErr={fail} />
  );

  const shown = (docs || []).filter(d => !q || `${d.doc_number} ${d.subject} ${d.sender_name} ${(d.recipients || []).map(r => r.name).join(' ')}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--ink)' }}>Албан бичиг</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          <Chip on={tab === 'out'} onClick={() => setTab('out')}>Явсан бичиг</Chip>
          <Chip on={tab === 'in'} onClick={() => setTab('in')}>Ирсэн бичиг</Chip>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <Input placeholder="Хайх…" value={q} onChange={e => setQ(e.target.value)} style={{ width: 200 }} />
          <Button icon="plus" onClick={() => setOpen({ doc_number: cfg?.next_number, doc_date: new Date().toISOString().slice(0, 10), page_count: 1, recipients: [], blank_type: 'A4', doc_kind: 'Албан бичиг', signer_title: 'Гүйцэтгэх захирал', status: 'draft' })}>
            {tab === 'out' ? 'Шинэ бичиг' : 'Ирсэн бичиг бүртгэх'}
          </Button>
        </div>
      </div>

      {msg && <Note icon="check">{msg}</Note>}
      {err && <Note tone="danger" icon="alert">{err}</Note>}
      {cfg && !cfg.digidoc_enabled && <Note tone="warn" icon="info">{cfg.hint}</Note>}
      {cfg && cfg.docx_system_enabled && !cfg.docx_linked && <DocxLinkCard onDone={() => { load(); note('Docx токен холбогдлоо'); }} onErr={fail} />}
      {cfg && cfg.docx_linked && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, color: 'var(--muted)' }}>
          <span>Docx холбогдсон · байгууллагын id {cfg.docx_org_id}</span>
          <Button size="sm" variant="secondary" loading={busy === 'inbox'} onClick={() => sync('inbox')}>Ирсэн бичиг татах</Button>
          <Button size="sm" variant="secondary" loading={busy === 'sent'} onClick={() => sync('sent')}>Явсан бичгийн төлөв шинэчлэх</Button>
          <Button size="sm" variant="ghost" style={{ color: 'var(--red)' }} onClick={unlink}>Салгах</Button>
        </div>
      )}

      <Card>
        {docs === null ? <Skeleton h={120} /> : shown.length === 0 ? (
          <Empty icon="search" title={tab === 'out' ? 'Явсан бичиг алга' : 'Ирсэн бичиг алга'} text="«Шинэ бичиг» дарж албан бичгээ зохионо." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead><tr style={{ textAlign: 'left', color: 'var(--muted)', fontSize: 13 }}>
                <th style={th}>Дугаар</th><th style={th}>Огноо</th><th style={th}>Тэргүү</th>
                <th style={th}>{tab === 'out' ? 'Хүлээн авагч' : 'Илгээгч'}</th><th style={th}>Төлөв</th><th style={th}></th>
              </tr></thead>
              <tbody>{shown.map(d => (
                <tr key={d.id} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={td}><b>{d.doc_number || '—'}</b><div style={{ fontSize: 12, color: 'var(--muted)' }}>{d.doc_kind}</div></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(d.doc_date)}</td>
                  <td style={{ ...td, cursor: 'pointer' }} onClick={() => setOpen(d)}>
                    <span style={{ fontWeight: 600 }}>{d.subject}</span>
                    {d.send_error && <div style={{ fontSize: 12, color: 'var(--red)' }}>{d.send_error}</div>}
                    {d.has_file && <div style={{ fontSize: 12, color: 'var(--muted)' }}>📎 {d.file_name}</div>}
                  </td>
                  <td style={td}>{tab === 'out' ? (d.recipients || []).map(r => r.name).join(', ') || '—' : d.sender_name || '—'}</td>
                  <td style={td}>
                    <Chip tone={ST[d.status]?.tone || 'new'}>{ST[d.status]?.label || d.status}</Chip>
                    {d.status === 'sent' && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }} title={d.resolution_note || ''}>{d.resolution_note || RES[d.resolution_status] || ''}</div>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Button size="sm" variant="ghost" onClick={() => setOpen(d)}>Нээх</Button>
                    {d.has_file && <Button size="sm" variant="ghost" onClick={() => openFile(d)}>PDF</Button>}
                    {tab === 'in' && d.status !== 'resolved' && <Button size="sm" variant="secondary" loading={busy === d.id} onClick={() => setStatus(d, 3)}>Шийдвэрлэсэн</Button>}
                    {d.status !== 'sent' && <Button size="sm" variant="ghost" style={{ color: 'var(--red)' }} onClick={() => remove(d)}>Устгах</Button>}
                  </td>
                </tr>))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ─────────────────────────  ЗАСВАРЛАГЧ  ───────────────────────── */

function DocEditor({ doc, cfg, direction, onClose, onMsg, onErr }:
  { doc: Partial<Doc>; cfg: Cfg | null; direction: 'out' | 'in'; onClose: () => void; onMsg: (m: string) => void; onErr: (e: any, d: string) => void }) {
  const [d, setD] = useState<Partial<Doc>>(doc);
  const [busy, setBusy] = useState('');
  const [full, setFull] = useState(false);
  const ed = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const step = STEPS.findIndex(s => s.key === (d.status || 'draft'));

  useEffect(() => { if (ed.current && ed.current.innerHTML !== (d.body || '')) ed.current.innerHTML = d.body || ''; /* eslint-disable-next-line */ }, [d.id]);

  const set = (p: Partial<Doc>) => setD(prev => ({ ...prev, ...p }));
  // Фонт/хэмжээний <select> фокусыг авахад бичвэр дэх сонголт алдагддаг тул сүүлийн байрлалыг хадгалж сэргээнэ
  const lastRange = useRef<Range | null>(null);
  const rememberRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ed.current && ed.current.contains(sel.getRangeAt(0).commonAncestorContainer)) lastRange.current = sel.getRangeAt(0).cloneRange();
  };
  const cmd = (c: string, v?: string) => {
    const el = ed.current; if (!el) return;
    const sel = window.getSelection();
    const inside = !!(sel && sel.rangeCount && el.contains(sel.getRangeAt(0).commonAncestorContainer));
    if (!inside) {
      el.focus();
      if (sel) {
        sel.removeAllRanges();
        if (lastRange.current) sel.addRange(lastRange.current);
        else { const r = document.createRange(); r.selectNodeContents(el); r.collapse(false); sel.addRange(r); }   // төгсгөлд
      }
    }
    document.execCommand(c, false, v);
    rememberRange();
    set({ body: el.innerHTML || '' });
  };

  const save = async (extra?: Partial<Doc>, silent?: boolean): Promise<Doc | null> => {
    const cur = { ...d, body: ed.current?.innerHTML ?? d.body, ...(extra || {}) };
    if (!(cur.subject || '').trim()) { onErr(null, 'Тэргүү (товч утга) заавал'); return null; }
    setBusy('save');
    try {
      const body = {
        direction, doc_number: cur.doc_number, doc_date: cur.doc_date, subject: cur.subject, body: cur.body,
        page_count: cur.page_count || 1, blank_type: cur.blank_type, doc_kind: cur.doc_kind,
        reviewer_name: cur.reviewer_name, signer_name: cur.signer_name, signer_title: cur.signer_title,
        recipients: cur.recipients || [], sender_name: cur.sender_name, status: cur.status,
      };
      const r: Doc = cur.id ? await api.put(`/admin/official-documents/${cur.id}`, body) : await api.post('/admin/official-documents', body);
      setD(r); if (!silent) onMsg('Хадгаллаа');
      return r;
    } catch (e) { onErr(e, 'Хадгалж чадсангүй'); return null; } finally { setBusy(''); }
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    let id = d.id;
    if (!id) { const r = await save(undefined, true); id = r?.id; if (!id) return; }
    setBusy('file');
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await fetch(`${API_ORIGIN}/api/v1/admin/official-documents/${id}/file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}`, ...(localStorage.getItem('platform_tenant_id') ? { 'X-Tenant-Id': localStorage.getItem('platform_tenant_id') as string } : {}) },
        body: fd,
      });
      const j = await r.json(); if (!r.ok) throw new Error(j.detail || 'Хавсаргахад алдаа');
      setD(j); onMsg('Тоон гарын үсэгтэй PDF хавсаргалаа');
    } catch (er) { onErr(er, 'Хавсаргаж чадсангүй'); } finally { setBusy(''); }
  };

  const send = async () => {
    const r = await save(undefined, true); if (!r) return;
    const manual = !cfg?.digidoc_enabled;
    if (!window.confirm(manual ? 'Энэ бичгийг «илгээсэн» гэж тэмдэглэх үү?' : 'Docx (docx.gov.mn)-оор хүлээн авагч байгууллагууд руу илгээх үү?')) return;
    setBusy('send');
    try { const x: any = await api.post(`/admin/official-documents/${r.id}/send`, { manual }); setD(x); onMsg(manual ? 'Илгээсэн гэж тэмдэглэв' : 'Docx-оор илгээлээ'); }
    catch (e) { onErr(e, 'Илгээж чадсангүй'); } finally { setBusy(''); }
  };

  const locked = d.status === 'sent';

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* шатлал */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflowX: 'auto', background: 'var(--card, #fff)', border: '1px solid var(--line)', borderRadius: 12, padding: '10px 12px' }}>
        <Button size="sm" variant="ghost" onClick={onClose}>← Жагсаалт</Button>
        {STEPS.map((s, i) => (
          <React.Fragment key={s.key}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 12px', borderRadius: 20, whiteSpace: 'nowrap',
              fontSize: 12, fontWeight: 700, letterSpacing: .3,
              background: i === step ? 'var(--green, #2f7d52)' : i < step ? '#e6f0ea' : 'transparent',
              color: i === step ? '#fff' : i < step ? 'var(--green, #2f7d52)' : 'var(--muted)',
            }}>
              <span style={{ width: 14, height: 14, borderRadius: 7, border: `2px solid ${i === step ? '#fff' : i < step ? 'var(--green, #2f7d52)' : 'var(--line)'}`, display: 'inline-block' }} />
              {s.label}
            </span>
            {i < STEPS.length - 1 && <span style={{ color: 'var(--line)', fontSize: 16 }}>›</span>}
          </React.Fragment>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Button size="sm" variant="secondary" onClick={() => printDoc({ ...d, body: ed.current?.innerHTML || d.body || '' } as Doc, cfg)}>PDF харах</Button>
          {!locked && <Button size="sm" loading={busy === 'save'} onClick={() => save()}>Хадгалах</Button>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: full ? '1fr' : 'minmax(320px, 400px) 1fr', gap: 12, alignItems: 'start' }}>
        {/* зүүн: мэдээлэл */}
        {!full && (
          <Card style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <b style={{ fontSize: 15 }}>{direction === 'out' ? 'Албан бичиг' : 'Ирсэн бичиг'}</b>
              <Chip tone={ST[d.status || 'draft']?.tone || 'new'}>{ST[d.status || 'draft']?.label}</Chip>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Бланкны төрөл">
                <select value={d.blank_type || 'A4'} disabled={locked} onChange={e => set({ blank_type: e.target.value, page_count: e.target.value === 'A5' ? 0.5 : 1 })} style={sel}>
                  <option value="A4">Хэвлэмэл хуудас A4</option><option value="A5">Хэвлэмэл хуудас A5</option>
                </select>
              </Field>
              <Field label="Бичгийн төрөл">
                <select value={d.doc_kind || 'Албан бичиг'} disabled={locked} onChange={e => set({ doc_kind: e.target.value })} style={sel}>
                  {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Тэргүү, товч утга"><Input value={d.subject || ''} disabled={locked} onChange={e => set({ subject: e.target.value })} placeholder="… тухай" /></Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <Field label="Дугаар"><Input value={d.doc_number || ''} disabled={locked} onChange={e => set({ doc_number: e.target.value })} /></Field>
              <Field label="Огноо"><Input type="date" value={(d.doc_date || '').slice(0, 10)} disabled={locked} onChange={e => set({ doc_date: e.target.value })} /></Field>
              <Field label="Хуудас"><Input type="number" step="0.5" value={d.page_count ?? 1} disabled={locked} onChange={e => set({ page_count: parseFloat(e.target.value) || 1 })} /></Field>
            </div>

            {direction === 'out'
              ? <RecipientPicker value={d.recipients || []} disabled={locked} onChange={v => set({ recipients: v })} />
              : <Field label="Илгээгч байгууллага"><Input value={d.sender_name || ''} onChange={e => set({ sender_name: e.target.value })} /></Field>}

            <Field label="Хавсралт файл" help="Тоон гарын үсгээр баталгаажсан PDF">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {d.has_file
                  ? <span style={{ fontSize: 13 }}>📎 {d.file_name}</span>
                  : <span style={{ fontSize: 13, color: 'var(--muted)' }}>Хавсаргаагүй</span>}
                {!locked && <Button size="sm" variant="secondary" loading={busy === 'file'} onClick={() => fileRef.current?.click()}>Файл хавсаргах</Button>}
              </div>
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Гарын үсэг зурах"><Input value={d.signer_name || ''} disabled={locked} onChange={e => set({ signer_name: e.target.value })} placeholder="СӨХ-ийн захирал" /></Field>
              <Field label="Албан тушаал"><Input value={d.signer_title || ''} disabled={locked} onChange={e => set({ signer_title: e.target.value })} /></Field>
            </div>

            {direction === 'out' && !locked && (
              <Button block loading={busy === 'send'} onClick={send} disabled={!(d.recipients || []).length}>
                {cfg?.digidoc_enabled ? 'Docx-оор илгээх' : 'Илгээсэн гэж тэмдэглэх'}
              </Button>
            )}
            {locked && <Note icon="check">Илгээгдсэн — {d.sent_at ? fmtDate(d.sent_at) : ''} {d.digidoc_doc_no ? `· Docx №${d.digidoc_doc_no}` : ''}</Note>}
          </Card>
        )}

        {/* баруун: бичвэр */}
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', padding: '8px 10px', borderBottom: '1px solid var(--line)', background: 'var(--bg-soft, #f8faf9)' }}>
            <b style={{ fontSize: 13, marginRight: 8 }}>Бичвэр</b>
            <TB onClick={() => cmd('undo')} title="Буцаах">↶</TB>
            <TB onClick={() => cmd('redo')} title="Дахих">↷</TB>
            <Sep />
            <TB onClick={() => cmd('bold')} title="Тод"><b>B</b></TB>
            <TB onClick={() => cmd('italic')} title="Налуу"><i>I</i></TB>
            <TB onClick={() => cmd('underline')} title="Доогуур зураас"><u>U</u></TB>
            <Sep />
            <TB onClick={() => cmd('insertOrderedList')} title="Дугаарлах">1.</TB>
            <TB onClick={() => cmd('insertUnorderedList')} title="Цэглэх">•</TB>
            <Sep />
            <TB onClick={() => cmd('justifyLeft')} title="Зүүн">≡</TB>
            <TB onClick={() => cmd('justifyCenter')} title="Төв">≣</TB>
            <TB onClick={() => cmd('justifyRight')} title="Баруун">≡</TB>
            <TB onClick={() => cmd('justifyFull')} title="Тэгшлэх">☰</TB>
            <Sep />
            <select defaultValue="Arial" onChange={e => cmd('fontName', e.target.value)} style={sel2} title="Фонт">
              {['Arial', 'Times New Roman', 'Georgia', 'Verdana'].map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <select defaultValue="3" onChange={e => cmd('fontSize', e.target.value)} style={{ ...sel2, width: 64 }} title="Хэмжээ">
              <option value="2">10</option><option value="3">12</option><option value="4">14</option><option value="5">18</option><option value="6">24</option>
            </select>
            <TB onClick={() => cmd('removeFormat')} title="Формат арилгах">✕</TB>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <TB onClick={() => setFull(f => !f)} title="Дэлгэцэнд тэлэх">⛶</TB>
            </div>
          </div>
          <div
            ref={ed}
            contentEditable={!locked}
            suppressContentEditableWarning
            onInput={() => { rememberRange(); set({ body: ed.current?.innerHTML || '' }); }}
            onKeyUp={rememberRange} onMouseUp={rememberRange}
            style={{
              minHeight: full ? '70vh' : 560, padding: '28px 34px', outline: 'none', background: '#fff',
              fontFamily: '"Times New Roman", serif', fontSize: 15, lineHeight: 1.6, color: '#111',
            }}
          />
        </Card>
      </div>
      <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={onFile} />
    </div>
  );
}

function TB({ children, onClick, title }: { children: React.ReactNode; onClick: () => void; title: string }) {
  return <button type="button" title={title} onMouseDown={e => e.preventDefault()} onClick={onClick}
    style={{ minWidth: 30, height: 30, border: '1px solid var(--line)', background: '#fff', borderRadius: 7, cursor: 'pointer', fontSize: 13.5 }}>{children}</button>;
}
const Sep = () => <span style={{ width: 1, height: 20, background: 'var(--line)', margin: '0 4px' }} />;

/** Хүлээн авагч байгууллага — лавлахаас хайж сонгоно, олдохгүй бол шинээр нэмнэ. */
/** СӨХ-ийн docxOrgToken-ийг Docx-д холбох карт (docx.gov.mn-д байгууллага бүртгүүлж гэрээ байгуулсны дараа олгогддог). */
function DocxLinkCard({ onDone, onErr }: { onDone: () => void; onErr: (e: any, d: string) => void }) {
  const [tok, setTok] = useState('');
  const [pos, setPos] = useState('Гүйцэтгэх захирал');
  const [last, setLast] = useState('');
  const [first, setFirst] = useState('');
  const [busy, setBusy] = useState(false);
  const link = async () => {
    if (tok.trim().length < 10) { onErr(null, 'docxOrgToken оруулна уу'); return; }
    setBusy(true);
    try { await api.post('/admin/official-documents/docx/link', { docx_org_token: tok.trim(), emp_pos_name: pos, emp_last_name: last, emp_first_name: first }); onDone(); }
    catch (e) { onErr(e, 'Docx токен холбож чадсангүй'); } finally { setBusy(false); }
  };
  return (
    <Card style={{ display: 'grid', gap: 10 }}>
      <div><b>Docx токен холбох</b><div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
        docx.gov.mn → «Байгууллага бүртгүүлэх гарын авлага»-ын дагуу СӨХ-өө бүртгүүлж гэрээ байгуулахад <b>docxOrgToken</b> олгоно. Түүнийг энд оруулснаар албан бичгээ бусад байгууллага руу Docx-оор шууд илгээж, ирсэн бичгээ татна.</div></div>
      <Field label="docxOrgToken"><Input value={tok} onChange={e => setTok(e.target.value)} placeholder="eyJhbGciOi…" /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <Field label="Албан тушаал"><Input value={pos} onChange={e => setPos(e.target.value)} /></Field>
        <Field label="Овог"><Input value={last} onChange={e => setLast(e.target.value)} /></Field>
        <Field label="Нэр"><Input value={first} onChange={e => setFirst(e.target.value)} /></Field>
      </div>
      <div><Button loading={busy} onClick={link}>Холбох</Button></div>
    </Card>
  );
}

function RecipientPicker({ value, onChange, disabled }: { value: Rec[]; onChange: (v: Rec[]) => void; disabled?: boolean }) {
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { api.get<any[]>(`/admin/official-documents/orgs?q=${encodeURIComponent(q)}`).then(setOpts).catch(() => setOpts([])); }, 250);
    return () => clearTimeout(t);
  }, [q, open]);
  const add = (o: any) => { if (!value.some(v => v.name === o.name)) onChange([...value, { name: o.name, reg_no: o.reg_no, branch_id: o.branch_id }]); setQ(''); setOpen(false); };
  const addNew = async () => {
    if (!q.trim()) return;
    try { const o: any = await api.post('/admin/official-documents/orgs', { name: q.trim() }); add(o); } catch { add({ name: q.trim(), reg_no: '', branch_id: 0 }); }
  };
  return (
    <Field label="Хаашаа (хүлээн авагч байгууллага)">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        {value.map((r, i) => (
          <span key={i} style={{ background: '#eef2f0', borderRadius: 8, padding: '4px 8px', fontSize: 13 }}>
            {r.name}{!r.branch_id && <span style={{ color: 'var(--muted)' }} title="Docx-д бүртгэлгүй — гараар илгээнэ"> · Docx-гүй</span>}
            {!disabled && <button onClick={() => onChange(value.filter((_, k) => k !== i))} style={{ marginLeft: 6, border: 0, background: 'none', cursor: 'pointer', color: 'var(--muted)' }}>✕</button>}
          </span>
        ))}
      </div>
      {!disabled && <Input placeholder="＋ Энд дарж хүлээн авах байгууллага оруулна уу!" value={q} onFocus={() => setOpen(true)} onChange={e => { setQ(e.target.value); setOpen(true); }} />}
      {open && !disabled && (
        <div style={{ border: '1px solid var(--line)', borderRadius: 8, marginTop: 4, maxHeight: 190, overflowY: 'auto', background: '#fff' }}>
          {opts.map(o => (
            <div key={o.id} onClick={() => add(o)} style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13.5, borderBottom: '1px solid var(--line)' }}>
              {o.name} {o.reg_no && <span style={{ color: 'var(--muted)' }}>· {o.reg_no}</span>}
            </div>
          ))}
          {q.trim() && !opts.some(o => o.name === q.trim()) && (
            <div onClick={addNew} style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13.5, color: 'var(--green, #2f7d52)' }}>＋ «{q.trim()}» нэрээр шинээр нэмэх</div>
          )}
        </div>
      )}
    </Field>
  );
}

/** Хэвлэх/PDF — СӨХ-ийн хэвлэмэл толгойтой A4/A5 бичиг. */
function printDoc(d: Doc, cfg: Cfg | null) {
  const esc = (s: string) => String(s || '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
  const to = (d.recipients || []).map(r => r.name).join(', ');
  const size = (d.blank_type || 'A4') === 'A5' ? 'A5' : 'A4';
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(d.doc_number)} ${esc(d.subject)}</title>
<style>@page{size:${size};margin:20mm 18mm}body{font-family:"Times New Roman",serif;font-size:13pt;line-height:1.55;color:#000;margin:0}
.hd{text-align:center;border-bottom:2px solid #000;padding-bottom:8px;margin-bottom:14px}.hd h1{font-size:15pt;margin:0 0 3px;text-transform:uppercase}
.hd .m{font-size:10pt;color:#333}.meta{display:flex;justify-content:space-between;font-size:11.5pt;margin-bottom:12px}
.to{margin-bottom:10px}.subj{text-align:center;font-weight:700;margin:14px 0}.bd p{margin:0 0 9px;text-align:justify}
.sign{margin-top:36px;display:flex;justify-content:space-between;align-items:flex-end}.sign .t{font-weight:700}
.note{margin-top:26px;font-size:9.5pt;color:#555;border-top:1px dashed #999;padding-top:6px}</style></head><body>
<div class="hd"><h1>${esc(cfg?.org_name || '')}</h1><div class="m">${esc(cfg?.address || '')}${cfg?.phone ? ' · утас: ' + esc(cfg.phone) : ''}${cfg?.reg_no ? ' · регистр: ' + esc(cfg.reg_no) : ''}</div></div>
<div class="meta"><span>${esc(d.doc_date)}</span><span>№ ${esc(d.doc_number)}</span></div>
<div class="to">${esc(to)}<br><b>-д</b></div>
<div class="subj">${esc(d.subject)}</div>
<div class="bd">${d.body || ''}</div>
<div class="sign"><div><div class="t">${esc(d.signer_title || '')}</div><div>................................ <b>${esc(d.signer_name || '')}</b></div></div><div>(тамга)</div></div>
<div class="note">Энэхүү бичгийг тоон гарын үсгээр баталгаажуулж PDF хэлбэрээр системд хавсаргана уу.</div>
<script>window.onload=function(){setTimeout(function(){window.print()},250)}</script></body></html>`;
  const w = window.open('', '_blank', 'width=860,height=940'); if (!w) return; w.document.write(html); w.document.close();
}

const th: React.CSSProperties = { padding: '8px 10px', fontWeight: 600 };
const td: React.CSSProperties = { padding: '9px 10px', verticalAlign: 'top' };
const sel: React.CSSProperties = { width: '100%', padding: '9px 10px', border: '1.5px solid var(--line)', borderRadius: 10, fontSize: 14, background: '#fff', boxSizing: 'border-box' };
const sel2: React.CSSProperties = { padding: '5px 6px', border: '1px solid var(--line)', borderRadius: 7, fontSize: 12.5, background: '#fff', height: 30 };
